import './index.scss';

import { ChangeEvent, ClipboardEvent, Component, FocusEvent, ForwardedRef, ReactNode } from 'react';
import MaskedInput, { MaskedInputProps } from 'react-text-mask';

import { UncontrolledField } from '..';
import { BraxIcon } from '../../BraxIcon';
import { BraxUncontrolledField } from '../BraxUncontrolledField';

type MaskArray = (string | RegExp)[];

export type BraxFieldMaskPattern = MaskArray | string | ((value: string) => MaskArray) | ((value: string) => string)

export type BraxFieldMaskObject = Pick<MaskedInputProps, 'guide' | 'placeholderChar' | 'keepCharPositions' | 'pipe' | 'showMask'> & {
    mask: BraxFieldMaskPattern
};

export type BraxFieldMask = BraxFieldMaskObject | BraxFieldMaskPattern;

export type BraxUncontrolledTextFieldProps = UncontrolledField<string> & {
    forwardRef?: ForwardedRef<HTMLInputElement>,
    label?: ReactNode,
    placeholder?: string,
    hint?: ReactNode,
    type?: 'text' | 'number' | 'url' | 'email' | 'color' | 'tel' | 'password' | 'search',
    step?: number | 'any',
    maxLength?: number,
    mask?: BraxFieldMask,
    required?: boolean,
    autoComplete?: string,
    onPaste?: (value: string, event: ClipboardEvent<HTMLInputElement>) => string | void,
    onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void,
    onFocus?: (event: FocusEvent<HTMLInputElement>) => void,
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void
};

export class BraxUncontrolledTextField extends Component<BraxUncontrolledTextFieldProps> {

    static defaultProps = {
        disabled: false,
        status: null,
        type: 'text',
        mask: false
    };

    defaultMaskOptions: Partial<BraxFieldMaskObject> = {
        guide: false,
        placeholderChar: '\u2000',
        keepCharPositions: false,
        showMask: false
    };

    pasteOverwrite?: string | void;

    paste = (event: ClipboardEvent<HTMLInputElement>) => {
        this.pasteOverwrite = this.props.onPaste?.(event.clipboardData.getData('text/plain'), event);
    };

    change(event: ChangeEvent<HTMLInputElement>) {
        this.props.onChange?.(this.pasteOverwrite ?? event.target.value, event);

        this.pasteOverwrite = undefined;
    }

    createMask(mask: string): MaskArray {
        return mask.split('').map(char => char === '9' ? /\d/ : char === 'a' ? /[^\d]/ : char === '*' ? /./ : char);
    }

    parseMask(mask: BraxFieldMaskPattern) {
        return typeof mask === 'string'
            ? this.createMask(mask)
        : typeof mask === 'function'
            ? (value: string) => this.parseMask(mask(value)) as MaskArray
        : mask;
    }

    getMaskOptions(mask: BraxFieldMask): MaskedInputProps {
        let maskArray = this.parseMask(typeof mask === 'object' && !Array.isArray(mask) ? mask.mask : mask);

        return typeof mask === 'object' && !Array.isArray(mask)
            ? { ...this.defaultMaskOptions, ...mask, mask: maskArray }
            : { ...this.defaultMaskOptions, mask: maskArray };
    }

    render() {
        const { id, label, hint, status, required, value, onChange, onPaste, mask, forwardRef, ...props } = this.props;

        return (
            <BraxUncontrolledField id={id}>
                { ({ id }) => (
                    <div className={`brax-text-field ${status}`}>
                        <div>
                            <BraxIcon icon="exclamation-mark" />

                            { mask ? (
                                <MaskedInput
                                    id={id}
                                    value={value !== null && value !== undefined ? value : ''}
                                    onChange={(event) => this.change(event)}
                                    onPaste={this.paste}
                                    {...this.getMaskOptions(mask)}
                                    {...props}
                                    render={(ref, { value, ...props }) => (
                                      <input ref={ref} {...props} value={value} />
                                    )}
                                />
                            ) : (
                                <input
                                    ref={obj => typeof forwardRef === 'function' && forwardRef(obj)}
                                    id={id}
                                    value={value !== null && value !== undefined ? value : ''}
                                    onChange={(event) => this.change(event)}
                                    onPaste={this.paste}
                                    {...props}
                                />
                            ) }

                            { label ? (
                                <label htmlFor={id}>{ label } { required ? '*' : null }</label>
                            ) : null }
                        </div>

                        { hint ? (
                            <span className="hint">{ hint }</span>
                        ) : null }
                    </div>
                ) }
            </BraxUncontrolledField>
        );
    }

}
